import { api } from './api';
import { LessonProgress } from '../types/lesson';
import { 
  CourseProgressResponse, 
  ProgressOverviewResponse, 
  ProgressStatsResponse 
} from '../types/progress';

// Progress interfaces
export interface CourseProgressItem {
  courseId: string;
  courseTitle: string;
  progress: number;
  completedLessons: number;
  totalLessons: number;
}

/**
 * Get progress for a specific course
 */
export const getCourseProgress = async (courseId: string): Promise<CourseProgressResponse> => {
  try {
    const response = await api.get<CourseProgressResponse>(`/api/Progress/course/${courseId}`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching progress for course ${courseId}:`, error);
    // Return default values if API call fails
    return {
      progress: 0,
      completedLessons: 0,
      totalLessons: 0
    };
  }
};

/**
 * Get progress for a specific lesson
 */
export const getLessonProgress = async (lessonId: string): Promise<LessonProgress | null> => {
  try {
    const response = await api.get<LessonProgress>(`/api/Progress/lesson/${lessonId}`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching progress for lesson ${lessonId}:`, error);
    return null;
  }
};

/**
 * Mark a lesson as complete
 */
export const completeLesson = async (lessonId: string): Promise<void> => {
  try {
    await api.post(`/api/Progress/lesson/${lessonId}/complete`);
  } catch (error) {
    console.error(`Error completing lesson ${lessonId}:`, error);
    throw error;
  }
};

/**
 * Get an overview of all progress
 */
export const getProgressOverview = async (): Promise<ProgressOverviewResponse> => {
  try {
    const response = await api.get<ProgressOverviewResponse>('/api/Progress/overview');
    return response.data;
  } catch (error) {
    console.error('Error fetching progress overview:', error);
    // Return default values if API call fails
    return {
      courseProgress: [],
      totalProgress: 0
    };
  }
};

/**
 * Get statistics about progress
 */
export const getProgressStats = async (): Promise<ProgressStatsResponse> => {
  try {
    const response = await api.get<ProgressStatsResponse>('/api/Progress/stats');
    return response.data;
  } catch (error) {
    console.error('Error fetching progress stats:', error);
    // Return default values if API call fails
    return {
      totalCourses: 0,
      completedCourses: 0,
      totalLessons: 0,
      completedLessons: 0,
      certificates: 0
    };
  }
};

/**
 * Reset all progress for a specific course
 */
export const resetCourseProgress = async (courseId: string): Promise<void> => {
  try {
    await api.post(`/api/Progress/course/${courseId}/reset`);
  } catch (error) {
    console.error(`Error resetting progress for course ${courseId}:`, error);
    throw error;
  }
}; 